import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { occupantControls, partySizeConflict } from '../../../lib/occupants';
import OccupantSearch from './OccupantSearch';
import type {
  CreateRoomOccupantGuestRequest,
  GroupInvitee,
  GroupRoomRequest,
  LinkRoomOccupantRequest,
  RoomOccupantsResponse,
} from '../../../types/groups';

/** The add the host last asked for, so a party-size clash can be retried as-is. */
type PendingAdd =
  | { kind: 'link'; body: LinkRoomOccupantRequest }
  | { kind: 'guest'; body: CreateRoomOccupantGuestRequest };

/**
 * Who is sleeping in one reservation.
 *
 * Until now a room had a single guest, so two people in one bed was either two
 * reservations for one hotel record — two confirmation numbers, two rates, one
 * room — or one reservation where the roommate simply did not exist: no
 * confirmation, nothing on their trip page, and no way for a host to see they
 * were accounted for.
 *
 * The rate is the reason this screen explains itself so loudly. Exactly one
 * occupant is the primary, and the primary is the only person whose confirmation
 * and trip page carry the money. A host who does not know that picks whoever
 * they typed first.
 */
export default function ReservationOccupantsDialog({
  groupId,
  request,
  blockLabel,
  invitees,
  onClose,
  onChanged,
}: {
  groupId: string;
  request: GroupRoomRequest;
  blockLabel: string;
  invitees: GroupInvitee[];
  onClose: () => void;
  /** Membership changes set resend_needed on the reservation, so the group payload is stale. */
  onChanged: () => void;
}) {
  const [lastAdd, setLastAdd] = useState<PendingAdd | null>(null);

  const occupantsQuery = useQuery<RoomOccupantsResponse>(
    ['room-occupants', groupId, request.id],
    () => groupsApi.listRoomOccupants(groupId, request.id),
  );

  const settled = () => {
    occupantsQuery.refetch();
    onChanged();
  };
  const linkMutation = useMutation(
    (body: LinkRoomOccupantRequest) => groupsApi.linkRoomOccupant(groupId, request.id, body),
    { onSuccess: settled },
  );
  const guestMutation = useMutation(
    (body: CreateRoomOccupantGuestRequest) => groupsApi.createRoomOccupantGuest(groupId, request.id, body),
    { onSuccess: settled },
  );
  const primaryMutation = useMutation(
    (occupantId: string) => groupsApi.setRoomOccupantPrimary(groupId, request.id, occupantId),
    { onSuccess: settled },
  );
  const removeMutation = useMutation(
    (occupantId: string) => groupsApi.removeRoomOccupant(groupId, request.id, occupantId),
    { onSuccess: settled },
  );

  const saving = linkMutation.isLoading || guestMutation.isLoading
    || primaryMutation.isLoading || removeMutation.isLoading;
  const error = [linkMutation, guestMutation, primaryMutation, removeMutation]
    .map((mutation) => mutationErrorMessage(mutation.error))
    .find(Boolean) || '';
  const conflict = partySizeConflict(linkMutation.error) || partySizeConflict(guestMutation.error);

  const add = (pending: PendingAdd) => {
    setLastAdd(pending);
    // Clear the other door's failure first. Both add to the same room, and a 409
    // the host walked away from would otherwise be shown against the add they
    // just made — including a "raise the head count" offer for the wrong person.
    if (pending.kind === 'link') {
      guestMutation.reset();
      linkMutation.mutate(pending.body);
    } else {
      linkMutation.reset();
      guestMutation.mutate(pending.body);
    }
  };
  /** Re-send the add the host just tried, carrying the head count they agreed to. */
  const retryWithPartySize = (partySize: number) => {
    if (!lastAdd) return;
    if (lastAdd.kind === 'link') add({ kind: 'link', body: { ...lastAdd.body, partySize } });
    else add({ kind: 'guest', body: { ...lastAdd.body, partySize } });
  };

  const occupants = occupantsQuery.data?.occupants ?? [];
  const partySize = occupantsQuery.data?.partySize ?? request.partySize;
  const namedCount = occupantsQuery.data?.namedOccupantCount ?? occupants.length;
  const unnamed = Math.max(partySize - namedCount, 0);

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      {/* Subtitle lives in the content, not the title: DialogTitle renders an
          <h2>, and a heading may only contain phrasing content. */}
      <DialogTitle sx={{ pb: 0.5 }}>Who’s in this room</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{request.guestName} · {blockLabel}</Typography>
          {conflict ? (
            <Alert
              severity="warning"
              action={
                lastAdd ? (
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => retryWithPartySize(conflict.suggestedPartySize)}
                  >
                    Raise to {conflict.suggestedPartySize} and add
                  </Button>
                ) : null
              }
            >
              This room is set for {conflict.partySize} traveller{conflict.partySize === 1 ? '' : 's'} and
              already has {conflict.namedOccupantCount} named. Raising it is what the hotel will be told.
            </Alert>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : null}

          {occupantsQuery.isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} /><Typography>Loading the room…</Typography>
            </Stack>
          ) : occupantsQuery.error ? (
            <Alert severity="error">Could not load who is in this room.</Alert>
          ) : (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {namedCount} named of {partySize} travelling
                </Typography>
                {unnamed > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    The other {unnamed} {unnamed === 1 ? 'is an unnamed plus-one' : 'are unnamed plus-ones'}.
                    Naming someone never changes the head count the hotel was given.
                  </Typography>
                ) : null}
              </Box>

              {occupants.length === 0 ? (
                <Alert severity="warning">
                  Nobody is named on this reservation, so its confirmation has no-one to go to.
                  Add the guest who booked it.
                </Alert>
              ) : (
                <Stack spacing={1.5} divider={<Divider flexItem />}>
                  {occupants.map((occupant) => {
                    const controls = occupantControls(occupants, occupant);
                    return (
                      <Box key={occupant.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700}>{occupant.fullName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {occupant.email || occupant.phone || 'No email or phone — they can’t be sent a confirmation'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {occupant.isPrimary
                              ? <Chip size="small" color="primary" label="Primary · sees the rate" />
                              : <Chip size="small" variant="outlined" label="Sharing · no rate" />}
                            {controls.canPromote ? (
                              <Button
                                size="small"
                                disabled={saving}
                                onClick={() => {
                                  // Who pays is guest-visible in both directions: this
                                  // person starts seeing the rate and the incumbent
                                  // stops. Worth a beat before it moves.
                                  if (!window.confirm(`Make ${occupant.fullName} the primary? They'll see the rate on their confirmation and trip page, and whoever is primary now will stop seeing it.`)) return;
                                  primaryMutation.mutate(occupant.id);
                                }}
                              >
                                Make primary
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              color="error"
                              disabled={saving || !controls.canRemove}
                              onClick={() => {
                                if (!window.confirm(`Take ${occupant.fullName} out of this room?`)) return;
                                removeMutation.mutate(occupant.id);
                              }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Box>
                        {controls.removeBlockedReason ? (
                          <Typography variant="caption" color="text.secondary">
                            {controls.removeBlockedReason}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              <Alert severity="info">
                Only the primary sees the price. Their confirmation and trip page carry the rate;
                everyone else in the room gets the hotel, the dates and the confirmation number, and
                never the amount. Pick the person who paid.
              </Alert>

              <Divider />

              {/*
                Remount on any membership change so the search box and the
                half-filled new-guest form clear after a write lands. A failed add
                keeps what was typed, which is the case where retyping hurts.
              */}
              <OccupantSearch
                key={occupants.length}
                roster={invitees}
                occupants={occupants}
                saving={saving}
                onLink={(inviteeId) => add({ kind: 'link', body: { inviteeId } })}
                onCreateGuest={(body) => add({ kind: 'guest', body })}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
